import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import type { Importer, Partners } from '@feathr/blackbox';
import { Alert } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import ImporterAlertMessage from './ImporterAlertMessage';

import * as styles from './ImportersAlert.css';

interface IProps {
  eventId: string;
  partnersCollection: Partners;
}

function ImportersAlert({ eventId, partnersCollection }: Readonly<IProps>) {
  const { Importers } = useStore();
  const [importers, setImporters] = useState<Importer[]>([]);

  useEffect(() => {
    async function loadImporters() {
      const loadedImporters = Importers.list(
        {
          filters: {
            event: eventId,
            state: 'pending',
          },
        },
        { reset: true },
      );
      await when(() => !loadedImporters.isPending);
      // Converting observables back to vanilla JavaScript.
      setImporters(loadedImporters.models.slice());
    }

    void loadImporters();
  }, []);

  useEffect(() => {
    async function refreshImporters(): Promise<void> {
      const loadedImporters = Importers.list({
        filters: {
          id__in: importers.map((importer) => importer.id),
        },
      });
      await when(() => !loadedImporters.isPending);
      // Converting observables back to vanilla JavaScript.
      setImporters(loadedImporters.models.slice());
    }

    // Keep refreshing until all importers have completed.
    let interval: NodeJS.Timeout;
    if (importers.length) {
      interval = setInterval(() => {
        void refreshImporters();
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [importers.length]);

  if (!importers.length) {
    return null;
  }

  function handleRefresh() {
    partnersCollection.refreshApiCache();
  }

  return (
    <Alert
      className={styles.alert}
      title={
        <>
          {importers.map((importer) => (
            <ImporterAlertMessage importer={importer} key={importer.id} onRefresh={handleRefresh} />
          ))}
        </>
      }
      type={'info'}
    />
  );
}

export default observer(ImportersAlert);
